import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Spinner } from "react-bootstrap";
import { cancelOrder, deleteOrder, getOrderByIssuerAccountId } from "../../service/api/Order";
import { FaTrash } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import ConfirmationModal from "../../Modal/ConfirmationModal";
import { toast } from "sonner";
import { useLoader } from "../../service/Hooks/useLoader";

// Composant pour l'en-tête du modal
const OrderModalHeader = ({ title, onClose }) => (
  <div className="modal-header">
    <h5 className="modal-title">{title}</h5>
    <button type="button" className="btn-close" onClick={onClose}></button>
  </div>
);

// Composant pour le corps du modal avec chargement, erreur ou table
const OrderModalBody = ({ loading, error, orders, formatDate, getStatusColor, handleCancelOrder }) => {
  if (loading) {
    return (
      <div className="text-center">
        <Spinner animation="border" variant="primary" />
        <p>Chargement des titres...</p>
      </div>
    );
  }

  if (error) {
    return <p className="text-danger">Erreur: {error}</p>;
  }

  if (orders.length === 0) {
    return <p className="text text-muted text-center">Aucun ordre trouvé pour ce compte.</p>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-striped align-middle">
        <thead>
          <tr>
            <th>Titre</th>
            <th>Marché</th>
            <th>Type Ordre</th>
            <th>Quantité</th>
            <th>Quantité Minimale</th>
            <th style={{ paddingLeft: "45px" }}>Prix</th>
            <th>Status</th>
            <th>Date creation</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id}>
              <td>{order.securityName}</td>
              <td>{order.targetMarketName}</td>
              <td className="text-center">
                <span className={`badge rounded-pill bg-${order.side === 'Buy' ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-2 py-2' : 'bg-danger bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-2 py-2'}`}>
                  {order.side}
                </span>
              </td>
              <td className="text-center">{order.quantity}</td>
              <td className="text-center">{order.minQuantity}</td>
              <td className="text-center text-nowrap">
                <span className="counter-amount-badge">
                  {new Intl.NumberFormat("fr-FR", { style: "decimal", minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true }).format(order.price || 0)} FCFA
                </span>
              </td>
              <td>
                <span className={`badge rounded-pill fw-medium px-3 py-2 ${getStatusColor(order.status)}`}>
                  {order.status}
                </span>
              </td>
              <td className="text-center">{formatDate(order.creationDate)}</td>
              <td>
                {order.status !== "Completed" && order.status !== "Canceled" && (
                  <a className="dropdown-item text-danger" href="#" onClick={(e) => { e.preventDefault(); handleCancelOrder(order.id); }} data-tooltip-id="supprimer-order-tooltip" data-tooltip-content={`Annuler l'ordre du client ${order.accountName}`} data-tooltip-place="top">
                    <FaTrash className="text-danger" />
                  </a>
                )}
                <Tooltip id="supprimer-order-tooltip" place="top" effect="solid" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Composant pour la pagination
const OrderPagination = ({ page, totalPages, setPage }) => (
  <nav>
    <ul className="pagination justify-content-center">
      <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
        <button className="page-link" onClick={() => setPage((prev) => Math.max(prev - 1, 1))}>
          <i className="fa-solid fa-angle-left"></i>
        </button>
      </li>
      {Array.from({ length: totalPages }, (_, i) => (
        <li key={i} className={`page-item ${page === i + 1 ? "active" : ""}`}>
          <button className="page-link" onClick={() => setPage(i + 1)}>
            {i + 1}
          </button>
        </li>
      ))}
      <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
        <button className="page-link" onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}>
          <i className="fa-solid fa-angle-right"></i>
        </button>
      </li>
    </ul>
  </nav>
);

// Composant principal ListeOrders
const ListeOrders = ({ fromAccountId, domainId, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showDeleteOrderModal, setShowDeleteOrderModal] = useState(false);
  const pageSize = 7;
  const {showLoader, hideLoader} = useLoader();

  useEffect(() => {
    if (!fromAccountId || !domainId) return;
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getOrderByIssuerAccountId(fromAccountId, domainId, page, pageSize);
        setOrders(data || []);
        setTotalPages(data.totalPages || Math.ceil((data.totalItems || 0) / pageSize) || 1);
      } catch (err) {
        setError(err.message || "Impossible de récupérer les Ordres.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [fromAccountId, domainId, page]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  const getStatusColor = (status) => {
    switch (status) {
      // États de succès - Vert
      case 'Completed':
      case 'Approved':
        return 'bg-success bg-opacity-10 text-success border border-success border-opacity-25';
      
      // États d'attente - Orange/Ambre
      case 'Pending':
      case 'PendingValidation':
      case 'PendingExecution':
        return 'bg-warning bg-opacity-10 text-warning-emphasis border border-warning border-opacity-25';
      
      // États d'échec/rejet - Rouge
      case 'Canceled':
      case 'Rejected':
      case 'ValidationRejected':
      case 'Echec Completed':
        return 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25';
      
      // État partiel - Bleu
      case 'Partial':
        return 'bg-info bg-opacity-10 text-info-emphasis border border-info border-opacity-25';
      
      // État brouillon - Gris
      case 'Brouillon':
        return 'bg-secondary bg-opacity-10 text-secondary-emphasis border border-secondary border-opacity-25';
      
      // Par défaut
      default:
        return 'bg-light text-dark border';
    }
  };


  const handleCancelDeleteOrder = () => { setOrderToDelete(null); setShowDeleteOrderModal(false); };

  const handleConfirmDeleteOrder = async () => {
    // Afficher le loader après validation
    showLoader({ message: "Suppresion en cours...", indeterminate: true });
    try {
      const success = await deleteOrder(orderToDelete.id);
      if (success) {
        toast.success("L'ordre a été supprimé avec succès !");
        setOrders((prev) => prev.filter(o => o.id !== orderToDelete.id));
      }
    } catch (err) {
      toast.error(err.message || "Erreur lors de la suppression.");
    } finally {
      setOrderToDelete(null);
      setShowDeleteOrderModal(false);
      hideLoader();
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      const success = await cancelOrder(orderId);
      if (success) {
        toast.error("L'ordre a été annulé.");
        setOrders((prev) => prev.map(o => o.id === orderId ? { ...o, status: "Canceled" } : o));
      }
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'annulation.");
    }
  };

  if (!fromAccountId || !domainId) return null;

  return ReactDOM.createPortal(
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)", display: "block" }}>
      <div className="modal-dialog modal-dialog-centered modal-custom">
        <div className="modal-content">
          <OrderModalHeader title="Liste des ordres" onClose={onClose} />
          <div className="modal-body">
            <OrderModalBody loading={loading} error={error} orders={orders} formatDate={formatDate} getStatusColor={getStatusColor} handleCancelOrder={handleCancelOrder} />
            {!loading && !error && orders.length > 0 && <OrderPagination page={page} totalPages={totalPages} setPage={setPage} />}
          </div>
        </div>
      </div>
      {showDeleteOrderModal && (
        <ConfirmationModal message="Êtes-vous sûr de vouloir supprimer cet ordre ?" onConfirm={handleConfirmDeleteOrder} onCancel={handleCancelDeleteOrder} />
      )}
    </div>,
    document.body
  );
};

export default ListeOrders;